(() => {
  const rootSelector = '#revico-staff-comment [data-layout-tag-name="staff-multi-comment"]';
  const base = 'revico-review-comment-detail--staff-recommend';
  const sliders = new WeakMap();
  const checkedLinks = new WeakSet();
  const requests = new Map();
  const queue = [];
  let activeRequests = 0;

  // REVICO opens a blank window in its own click handler, even without href.
  // Stop activation before that handler runs; leave touch/swipe events intact.
  function blockDisabledLink(event) {
    if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
    const link = event.target instanceof Element && event.target.closest(
      `${rootSelector} .${base}--product-info a[data-c-revico-link-state]`
    );
    if (!link) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }
  ['click', 'auxclick', 'keydown'].forEach(type => {
    document.addEventListener(type, blockDisabledLink, true);
  });

  // Share each result for this page only; never persist an unavailable status.
  function checkUrl(url) {
    if (!requests.has(url)) {
      requests.set(url, new Promise(resolve => {
        queue.push({ url, resolve });
        drain();
      }));
    }
    return requests.get(url);
  }

  function drain() {
    while (activeRequests < 3 && queue.length) {
      const { url, resolve } = queue.shift();
      activeRequests++;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 6000);
      fetch(url, { method: 'HEAD', credentials: 'same-origin', signal: controller.signal })
        .then(response => resolve(response.status === 404 || response.status === 410))
        .catch(() => resolve(false))
        .finally(() => {
          clearTimeout(timer);
          activeRequests--;
          drain();
        });
    }
  }

  function checkLink(link) {
    if (checkedLinks.has(link)) return;
    let url;
    try { url = new URL(link.href, location.href); } catch (_) { return; }
    if (url.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname)) return;
    checkedLinks.add(link);
    const original = {};
    ['href', 'target', 'tabindex', 'aria-disabled'].forEach(name => { original[name] = link.getAttribute(name); });
    url.hash = '';
    // Removing href also prevents opening an unchecked URL via the context menu.
    link.removeAttribute('href');
    link.removeAttribute('target');
    link.setAttribute('aria-disabled', 'true');
    link.setAttribute('tabindex', '-1');
    link.dataset.cRevicoLinkState = 'checking';
    checkUrl(url.href).then(unavailable => {
      if (unavailable) {
        link.dataset.cRevicoLinkState = 'unavailable';
      } else {
        Object.entries(original).forEach(([name, value]) => {
          if (value === null) link.removeAttribute(name);
          else link.setAttribute(name, value);
        });
        delete link.dataset.cRevicoLinkState;
      }
    });
  }

  function initSlider(box) {
    if (sliders.has(box)) return;
    const cards = Array.from(box.children);
    if (cards.length < 2) return;
    const nav = document.createElement('div');
    nav.className = 'c-revico-slider-nav';
    nav.setAttribute('role', 'group');
    nav.setAttribute('aria-label', 'スタッフのおすすめ商品の切り替え');
    const button = (label, text) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.setAttribute('aria-label', label);
      el.textContent = text;
      nav.append(el);
      return el;
    };
    const prev = button('前のおすすめ商品', '‹');
    const dots = cards.map((_, i) => {
      const dot = button(`おすすめ商品 ${i + 1} / ${cards.length} を表示`, '');
      dot.className = 'c-revico-slider-dot';
      return dot;
    });
    const next = button('次のおすすめ商品', '›');
    box.after(nav);
    box.setAttribute('tabindex', '0');
    box.setAttribute('aria-label', 'スタッフのおすすめ商品');
    let current = 0;
    const update = () => {
      if (!box.clientWidth) return;
      current = Math.max(0, Math.min(cards.length - 1, Math.round(box.scrollLeft / box.clientWidth)));
      dots.forEach((dot, i) => dot.setAttribute('aria-current', i === current ? 'true' : 'false'));
      prev.disabled = current === 0;
      next.disabled = current === cards.length - 1;
    };
    const go = index => box.scrollTo({
      left: Math.max(0, Math.min(cards.length - 1, index)) * box.clientWidth,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
    });
    prev.addEventListener('click', () => go(current - 1));
    next.addEventListener('click', () => go(current + 1));
    dots.forEach((dot, i) => dot.addEventListener('click', () => go(i)));
    box.addEventListener('scroll', update, { passive: true });
    box.addEventListener('keydown', event => {
      // The existing mobile slider owns its touch behavior and active dot.
      if (event.target !== box || getComputedStyle(nav).display === 'none') return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      go(event.key === 'Home' ? 0 : event.key === 'End' ? cards.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1));
    });
    let previousWidth = box.clientWidth;
    const resize = new ResizeObserver(() => {
      if (!box.isConnected) { resize.disconnect(); return; }
      if (box.clientWidth !== previousWidth && getComputedStyle(nav).display !== 'none') {
        box.scrollLeft = current * box.clientWidth;
      }
      previousWidth = box.clientWidth;
      update();
    });
    resize.observe(box);
    sliders.set(box, { nav, resize });
    update();
  }

  function scan() {
    document.querySelectorAll(rootSelector).forEach(root => {
      root.querySelectorAll(`.${base}--box`).forEach(box => {
        initSlider(box);
        // Native dots can arrive asynchronously, after the cards themselves.
        const nativeSlider = box.parentElement.querySelector('.revico--slider-wrapper');
        box.parentElement.classList.toggle('c-revico-has-native-slider', Boolean(nativeSlider));
      });
      root.querySelectorAll(`.${base}--product-info a[href]`).forEach(checkLink);
    });
  }

  let scheduled = false;
  const observer = new MutationObserver(records => {
    const relevant = records.some(record =>
      record.target instanceof Element && record.target.closest('#revico-staff-comment') ||
      Array.from(record.addedNodes).some(node => node instanceof Element &&
        (node.matches('#revico-staff-comment') || node.querySelector('#revico-staff-comment')))
    );
    if (!relevant) return;
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; scan(); });
  });
  observer.observe(document.body, { childList: true, subtree: true });
  scan();
})();
